import React, { memo, useMemo } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { SelectedChips } from './SelectedChips';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useDynamicFieldContext } from '../DynamicFieldContext';
import { normalizeSelectedValues } from '../utility/functions';

type SelectedItemsAccordionProps = {
  onRemove: (value: string) => void;
  includeChildren?: boolean;
  onIncludeChildrenChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps<Theme>;
  includeChildrenDisabled?: boolean;
  disabled?: boolean;
};

export const SelectedItemsAccordion = memo(
  ({
    onRemove,
    includeChildren = false,
    onIncludeChildrenChange,
    sx,
    includeChildrenDisabled = false,
    disabled = false,
  }: SelectedItemsAccordionProps) => {
    const theme = useTheme();
    const { selectedValues, fieldConfig } = useDynamicFieldContext();
    const { t } = useTranslation();

    // Normalize to string[] for render/handlers
    const normValues = useMemo(
      () => normalizeSelectedValues(selectedValues),
      [selectedValues],
    );

    // Resolve sx prop to object for spread
    const resolvedFieldSx: SxProps<Theme> = useMemo(() => {
      const computedSx = typeof sx === 'function' ? sx(theme) : sx;
      const resolvedSx = { ...computedSx };
      return resolvedSx;
    }, [sx, theme]);

    return (
      <Box data-testid="selected-items-accordion-wrapper">
        <Accordion
          disableGutters
          elevation={0}
          data-testid="selected-items-accordion-root"
          sx={{
            backgroundColor: 'transparent',
            ...resolvedFieldSx,
          }}
        >
          <AccordionSummary
            data-testid="selected-items-accordion-summary"
            expandIcon={<ExpandMoreIcon />}
            sx={{
              display: 'flex',
              alignItems: 'center',
              margin: 0,
              minHeight: 0,
              px: 1,
              bgcolor: theme.palette.action.hover,
              '&:hover': {
                bgcolor: theme.palette.action.selected,
              },
              borderRadius: '4px',
              // expanded blend bottom corners w/ collapsed area
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
              }}
            >
              {normValues.length} selected
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            data-testid="selected-items-accordion-details"
            sx={{
              p: 1,
              bgcolor: theme.palette.action.hover,
              // expanded blend bottom corners w/ summary area
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
            }}
          >
            <SelectedChips
              values={normValues}
              onRemove={onRemove}
              disabled={disabled}
            />
          </AccordionDetails>
        </Accordion>
        {fieldConfig?.config?.allow_include_children && (
          <FormControlLabel
            label={t('blox.general.include_children')}
            data-testid="include-children-label"
            control={
              <Checkbox
                data-testid="include-children-checkbox"
                disabled={includeChildrenDisabled}
                checked={includeChildren}
                onChange={onIncludeChildrenChange}
                size="small"
              />
            }
          />
        )}
      </Box>
    );
  },
);
SelectedItemsAccordion.displayName = 'SelectedItemsAccordion';
